import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Languages,
  ChevronDown,
  ChevronUp,
  FileText,
  Shield,
} from "lucide-react";
import { API } from "../../config";
import { useAuth } from "../../context/AuthContext";
import { CmsCoverageWidget } from "./CmsCoverageWidget";

// ─── Doc-type meta (only what the compact banner needs) ────────────────
// Aligned with DOC_TYPE_META inside CmsCoverageWidget.js — keep in sync
// when new doc types are added.
//
// Zorzutti CR 22/02/2026 — "annex" è stato rimosso dalla lista attiva
// perché nel flusso di firma corrente gli Allegati 1/2/3 vengono
// generati inline nel PDF del contratto (contract_v2_renderer.py) e
// non consumano il CMS. La riga sarebbe sempre "Allegati 0/18" e dava
// un'impressione allarmistica in demo con LoJack. Per ri-attivare il
// tipo, spostare la riga sotto commentata dentro DOC_TYPES.
const DOC_TYPES = [
  { key: "contract", label: "Contratto",         Icon: FileText,     color: "text-blue-400" },
  { key: "privacy",  label: "Privacy",           Icon: Shield,       color: "text-emerald-400" },
  // { key: "annex",    label: "Allegati",          Icon: FolderClosed, color: "text-purple-400" },
];

const LS_KEY = "cms_coverage_expanded_v1";

/**
 * Collapsible section for the three "Copertura linguistica CMS" widgets.
 *
 * Rationale (Zorzutti feedback 22/02/2026): the three full-width widgets
 * dominate the /admin/legal page and give an alarming "0/18" first
 * impression to enterprise reviewers, even when the underlying AI
 * translation cache is fully populated. We collapse them into a single
 * summary strip that still surfaces coverage counters at a glance, but
 * defers the full 18×3 matrix behind an "Espandi" toggle.
 *
 * Design goals:
 *   1. Zero disruption to the CmsCoverageWidget internals — we just
 *      render them lazily inside a collapsible container.
 *   2. Persist the user's expand/collapse preference in localStorage so
 *      power users (like Mara doing weekly audits) keep their view.
 *   3. Fetch only lightweight counters for the collapsed strip so the
 *      page still loads fast when the section is closed.
 */
export const CmsCoverageCollapsibleSection = ({ onChanged }) => {
  const { getAuthHeader } = useAuth();
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === "1"; }
    catch { return false; }
  });
  const [summary, setSummary] = useState(null); // {contract:{covered,total,pct}, privacy:{…}, annex:{…}}
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    try {
      const results = await Promise.all(
        DOC_TYPES.map((dt) =>
          axios.get(
            `${API}/admin/legal-documents/cms-coverage?doc_type=${dt.key}`,
            { headers: getAuthHeader() },
          ).then((r) => ({ key: dt.key, data: r.data })),
        ),
      );
      const next = {};
      for (const { key, data } of results) {
        const total = Array.isArray(data.by_language) ? data.by_language.length : 18;
        next[key] = {
          covered: data.covered || 0,
          total,
          pct: data.coverage_pct != null
            ? Number(data.coverage_pct)
            : (total ? Math.round(((data.covered || 0) / total) * 1000) / 10 : 0),
        };
      }
      setSummary(next);
    } catch {
      // Silent failure — the banner will just show "—/18" and the user
      // can still click Espandi to load the full widgets with proper
      // error handling.
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      try { localStorage.setItem(LS_KEY, next ? "1" : "0"); } catch {/* noop */}
      return next;
    });
  };

  const barPct = (() => {
    if (!summary) return 0;
    const total = DOC_TYPES.reduce((acc, dt) => acc + (summary[dt.key]?.total || 0), 0);
    const cov = DOC_TYPES.reduce((acc, dt) => acc + (summary[dt.key]?.covered || 0), 0);
    return total ? Math.round((cov / total) * 100) : 0;
  })();

  return (
    <div
      data-testid="cms-coverage-collapsible-section"
      className="rounded-2xl border border-gray-800/60 bg-gray-900/40 overflow-hidden"
    >
      {/* ── Compact banner (always visible) ── */}
      <button
        type="button"
        onClick={toggle}
        data-testid="cms-coverage-collapse-toggle"
        aria-expanded={expanded}
        className="w-full flex items-center gap-4 px-4 py-3 hover:bg-white/[0.03] transition-colors text-left"
      >
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-lojack-red/10 flex items-center justify-center">
          <Languages size={18} className="text-lojack-red" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <span className="text-sm font-semibold text-white">Copertura linguistica CMS</span>
            {loading ? (
              <span className="text-xs text-gray-500">caricamento…</span>
            ) : (
              <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-400">
                {DOC_TYPES.map((dt) => {
                  const s = summary?.[dt.key];
                  const label = s ? `${s.covered}/${s.total}` : "—/18";
                  const Icon = dt.Icon;
                  return (
                    <span
                      key={dt.key}
                      data-testid={`cms-coverage-banner-${dt.key}`}
                      className="inline-flex items-center gap-1.5"
                    >
                      <Icon size={12} className={dt.color} aria-hidden="true" />
                      <span className="text-gray-500">{dt.label}</span>
                      <span className="font-semibold text-gray-300">{label}</span>
                    </span>
                  );
                })}
              </span>
            )}
          </div>
          {/* Aggregate progress bar */}
          <div className="mt-1.5 h-1 w-full max-w-md bg-gray-800 rounded overflow-hidden">
            <div
              data-testid="cms-coverage-banner-progress"
              className="h-full bg-lojack-red transition-all duration-500"
              style={{ width: `${barPct}%` }}
            />
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2 text-xs text-gray-400">
          <span>{expanded ? "Comprimi" : "Espandi"}</span>
          {expanded
            ? <ChevronUp size={16} aria-hidden="true" />
            : <ChevronDown size={16} aria-hidden="true" />}
        </div>
      </button>

      {/* ── Expanded: full doc-type matrix (iterates DOC_TYPES so that
             hiding a type via the constant automatically removes both
             the banner counter AND its detail widget) ── */}
      {expanded && (
        <div
          data-testid="cms-coverage-expanded-panel"
          className="border-t border-gray-800/60 px-3 pt-3 pb-4 space-y-3 bg-black/20"
        >
          {DOC_TYPES.map((dt) => (
            <CmsCoverageWidget
              key={dt.key}
              docType={dt.key}
              onChanged={() => { onChanged?.(); fetchSummary(); }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CmsCoverageCollapsibleSection;
